import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DeleteLocationConfiguration,
  DeleteLocationConfigurationRequest,
  FindAllLocationConfigurationsForTenant,
  FindAllSalesSeriesConfigurationsForTenant,
  FindOrCreateLocationConfiguration,
  FindOrCreateSalesSeriesConfiguration,
  GetLocationConfigurables,
  GetSalesSeriesConfigurables,
  UpdateLocationConfiguration,
  UpdateLocationConfigurationRequest,
  UpdateSalesSeriesConfiguration,
  UpdateSalesSeriesConfigurationRequest,
} from 'src/api/trade'
import { useCompanyContext } from 'src/companies/routes/company-context'
import usePaginatedQuery from 'src/utils/microservice/paginated-query'

export const FIND_ALL_LOCATION_CONFIGURABLES_FOR_TENANT_QUERY_KEY =
  'find-all-location-configurables-for-tenant'
export function useFindAllLocationConfigurationsForTenant() {
  const { company } = useCompanyContext()
  return usePaginatedQuery({
    queryKey: [FIND_ALL_LOCATION_CONFIGURABLES_FOR_TENANT_QUERY_KEY, company.tenant_id],
    dataKey: 'locationConfigurations',
    queryFn: async ({ continuationToken, pageSize }) => {
      let [err, response] = await FindAllLocationConfigurationsForTenant({
        tenantId: company.tenant_id,
        continuationToken,
        pageSize,
      })
      if (err) throw err
      return response
    },
  })
}

export const FIND_ALL_SALES_SERIES_CONFIGURATIONS_FOR_TENANT_QUERY_KEY =
  'find-all-sales-series-configurations-for-tenant'
export function useFindAllSalesSeriesConfigurationsForTenant() {
  const { company } = useCompanyContext()
  return usePaginatedQuery({
    queryKey: [FIND_ALL_SALES_SERIES_CONFIGURATIONS_FOR_TENANT_QUERY_KEY, company.tenant_id],
    dataKey: 'salesSeriesConfigurations',
    queryFn: async ({ continuationToken, pageSize }) => {
      let [err, response] = await FindAllSalesSeriesConfigurationsForTenant({
        tenantId: company.tenant_id,
        continuationToken,
        pageSize,
      })
      if (err) throw err
      return response
    },
  })
}

export const GET_LOCATION_CONFIGURABLES = 'get-location-configurables'
export function useGetLocationConfigurables() {
  const { company } = useCompanyContext()
  return useQuery<TenantConfigResponse[]>({
    queryKey: [GET_LOCATION_CONFIGURABLES, company.tenant_id],
    queryFn: async () => {
      let [err, response] = await GetLocationConfigurables(company.tenant_id)
      if (err) throw err
      return response.configurables
    },
  })
}

export const GET_SALES_SERIES_CONFIGURABLES = 'get-sales-series-configurables'
export function useGetSalesSeriesConfigurables() {
  const { company } = useCompanyContext()
  return useQuery<TenantConfigResponse[]>({
    queryKey: [GET_SALES_SERIES_CONFIGURABLES, company.tenant_id],
    queryFn: async () => {
      let [err, response] = await GetSalesSeriesConfigurables(company.tenant_id)
      if (err) throw err
      return response.configurables
    },
  })
}

export const FIND_OR_CREATE_LOCATION_CONFIG_QUERY_KEY = 'find-or-create-location-configuration'
export function useFindOrCreateLocationConfiguration({ locationId }) {
  const { company } = useCompanyContext()
  return useQuery<KeyValuePair[]>({
    queryKey: [
      FIND_OR_CREATE_LOCATION_CONFIG_QUERY_KEY,
      { tenantId: company.tenant_id, locationId },
    ],
    queryFn: async () => {
      let [err, response] = await FindOrCreateLocationConfiguration({
        tenantId: company.tenant_id,
        locationId,
      })
      if (err) throw err
      return response?.locationConfiguration?.configurations
    },
  })
}

export const FIND_OR_CREATE_SALES_SERIES_CONFIG_QUERY_KEY =
  'find-or-create-sales-series-configuration'
export function useFindOrCreateSalesSeriesConfiguration({ salesSeriesId }) {
  const { company } = useCompanyContext()
  return useQuery<KeyValuePair[]>({
    queryKey: [
      FIND_OR_CREATE_SALES_SERIES_CONFIG_QUERY_KEY,
      { tenantId: company.tenant_id, salesSeriesId },
    ],
    queryFn: async () => {
      let [err, response] = await FindOrCreateSalesSeriesConfiguration({
        tenantId: company.tenant_id,
        salesSeriesId,
      })
      if (err) throw err
      return response?.salesSeriesConfiguration?.configurations
    },
    enabled: !!salesSeriesId,
  })
}

export function useUpdateLocationConfiguration() {
  const { company } = useCompanyContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateLocationConfigurationRequest) => {
      let [err, response] = await UpdateLocationConfiguration(data, company.tenant_id)
      if (err) throw err

      await queryClient.invalidateQueries({ queryKey: [FIND_OR_CREATE_LOCATION_CONFIG_QUERY_KEY] })

      return response.configurations
    },
  })
}

export function useDeleteLocationConfiguration() {
  const { company } = useCompanyContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: DeleteLocationConfigurationRequest) => {
      let [err, response] = await DeleteLocationConfiguration(data, company.tenant_id)
      if (err) throw err

      await queryClient.invalidateQueries({
        queryKey: [FIND_ALL_LOCATION_CONFIGURABLES_FOR_TENANT_QUERY_KEY],
      })

      return response
    },
  })
}

export function useUpdateSalesSeriesConfiguration() {
  const { company } = useCompanyContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateSalesSeriesConfigurationRequest) => {
      let [err, response] = await UpdateSalesSeriesConfiguration(data, company.tenant_id)
      if (err) throw err

      await queryClient.invalidateQueries({
        queryKey: [FIND_OR_CREATE_SALES_SERIES_CONFIG_QUERY_KEY],
      })

      return response.configurations
    },
  })
}
